// Shared helpers for the Quotations module (mirrors backend Decimal math for live preview)

export const Q_STATUS_COLORS = {
  Draft: 'bg-slate-100 text-slate-600 border-slate-200',
  Sent: 'bg-blue-50 text-blue-700 border-blue-200',
  Viewed: 'bg-amber-50 text-amber-700 border-amber-200',
  Accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-red-50 text-red-600 border-red-200',
  Expired: 'bg-rose-100 text-rose-800 border-rose-300',
  Converted: 'bg-violet-50 text-violet-700 border-violet-200',
};

export const Q_STATUSES = ['Draft', 'Sent', 'Viewed', 'Accepted', 'Rejected', 'Expired', 'Converted'];

export const TAX_PRESETS = [
  { value: 18, label: 'GST 18%' },
  { value: 5, label: 'GST 5%' },
  { value: 12, label: 'GST 12%' },
  { value: 28, label: 'GST 28%' },
  { value: 0, label: 'No Tax' },
];

// DD-MM-YYYY everywhere (Indian format)
export const fmtDMY = (d) => (d ? `${String(d).slice(8, 10)}-${String(d).slice(5, 7)}-${String(d).slice(0, 4)}` : '\u2014');

// ₹ with Indian grouping, always 2 decimals
export const inr2 = (n) =>
  `\u20b9 ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const newItemId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `item-${Date.now()}-${Math.random().toString(36).slice(2)}`);

export const blankItem = (taxRate = 18) => ({
  id: newItemId(),
  description: '', sub_description: '', hsn: '', qty: 1, unit: 'Units',
  unit_price: 0, tax_rate: taxRate, note: '', scope: [],
});

/** Guarantee every line item (and scope line) has a stable id for React list keys.
 * Scope lines are strings in the API; client-side we lift them to {id, text}. */
export const withItemIds = (items) => (items || []).map((it) => ({
  ...it,
  id: it.id || newItemId(),
  scope: (it.scope || []).map((s) => (typeof s === 'string' ? { id: newItemId(), text: s } : s)),
}));

/** Convert client-side items back to the API shape (scope objects -> strings). */
export const serializeItems = (items) => (items || []).map((it) => ({
  ...it,
  scope: (it.scope || []).map((s) => (typeof s === 'string' ? s : s.text)).filter((t) => String(t || '').trim()),
}));

export const newScopeLine = () => ({ id: newItemId(), text: '' });

const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Live client-side mirror of the backend GST engine (server stays authoritative). */
export const computeTotals = (items, placeOfSupply) => {
  const interstate = String(placeOfSupply || 'Assam').trim().toLowerCase() !== 'assam';
  let untaxed = 0, cgst = 0, sgst = 0, igst = 0;
  items.forEach((it) => {
    const amount = r2(Number(it.qty || 0) * Number(it.unit_price || 0));
    const tax = r2((amount * Number(it.tax_rate || 0)) / 100);
    untaxed = r2(untaxed + amount);
    if (interstate) {
      igst = r2(igst + tax);
    } else {
      const half = r2(tax / 2);
      cgst = r2(cgst + half);
      sgst = r2(sgst + r2(tax - half));
    }
  });
  return { untaxed, cgst, sgst, igst, grand_total: r2(untaxed + cgst + sgst + igst), is_interstate: interstate };
};

// ---------------- Indian amount-in-words (Lakhs / Crores) ----------------
const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven',
  'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const two = (n) => (n < 20 ? ONES[n] : TENS[Math.floor(n / 10)] + (n % 10 ? `-${ONES[n % 10]}` : ''));
const three = (n) => {
  const h = Math.floor(n / 100), r = n % 100;
  return [h ? `${ONES[h]} Hundred` : '', r ? two(r) : ''].filter(Boolean).join(' ');
};
const intWords = (n) => {
  if (n === 0) return 'Zero';
  const parts = [];
  const crores = Math.floor(n / 10000000);
  if (crores) parts.push(`${intWords(crores)} Crore`);
  n %= 10000000;
  const lakhs = Math.floor(n / 100000);
  if (lakhs) parts.push(`${two(lakhs)} Lakh`);
  n %= 100000;
  const th = Math.floor(n / 1000);
  if (th) parts.push(`${two(th)} Thousand`);
  n %= 1000;
  if (n) parts.push(three(n));
  return parts.join(' ');
};
export const amountInWords = (amount) => {
  const v = Math.round(Number(amount || 0) * 100) / 100;
  const rupees = Math.floor(v);
  const paise = Math.round((v - rupees) * 100);
  let w = `Rupees ${intWords(rupees)}`;
  if (paise) w += ` and Paise ${two(paise)}`;
  return `${w} Only`;
};

export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana',
  'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
  'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

/** Download an authenticated PDF via blob (keeps httpOnly cookie auth). */
export const downloadQuotationPdf = async (api, id, number) => {
  const res = await api.get(`/quotations/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${number || 'quotation'}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
};

/** 1-tap WhatsApp share: opens wa.me with the tracked quotation link pre-filled.
 * Opens the tab synchronously (inside the click gesture) to avoid popup blockers,
 * then points it at the wa.me URL returned by the server (which also logs + flips
 * Draft -> Sent). Returns the new status, or null on failure. */
export const shareQuotationWhatsApp = async (api, id) => {
  const win = window.open('', '_blank', 'noopener');
  try {
    const { data } = await api.post(`/quotations/${id}/whatsapp-share`, {});
    if (win) win.location = data.wa_url;
    else window.open(data.wa_url, '_blank', 'noopener');
    return data.status;
  } catch (e) {
    if (win) win.close();
    throw e;
  }
};
